import React from 'react';

import { Divider } from '@mui/material';

import { SearchType } from 'app/constants';

import useStyles from './styles';

import { useSearchField } from '../../hooks/use-search-field';
import { ChangeTrigger, SearchFieldValue } from '../../types';
import AutoCompleteInput from '../AutoCompleteInput';
import BaseInput from '../BaseInput';
import ClearButton from '../ClearButton';
import HotKey from '../HotKey';
import SearchButton from '../SearchButton';
import SearchTypeSelect from '../SearchTypeSelect';
import SubscriptionButton from '../SubscriptionButton';

interface Props {
    className?: string;
    inputClassName?: string;
    listClassName?: string;
    value?: SearchFieldValue;
    onChange?: (value: SearchFieldValue, trigger: ChangeTrigger) => void;
    onSubmit?: () => void;
    onInputFieldEnter?: () => void;
    onOptionClick?: (option: { value: string }, event: React.MouseEvent<HTMLLIElement>) => void;
    suggestions?: { value: string }[];
    loading?: boolean;
    autoCompleteInputRef?: React.RefObject<HTMLInputElement>;
    'data-testid'?: string;
}

const DesktopSearchField: React.FC<Props> = ({
    value,
    className,
    inputClassName,
    listClassName,
    onChange,
    onInputFieldEnter,
    onSubmit,
    onOptionClick,
    suggestions,
    loading,
    autoCompleteInputRef
}) => {
    const { classes, cx } = useStyles();

    const {
        type,
        query,
        software,
        version,
        errors,
        canClear,
        handleReset,
        handleTypeChange,
        handleQueryChange,
        handleSoftwareChange,
        handleVersionChange,
        handleAutoCompleteKeyDown,
        handleSubmit
    } = useSearchField({
        value,
        onChange,
        onInputFieldEnter,
        onSubmit,
        autoCompleteInputRef
    });

    return (
        <div className={cx(classes.container, className)}>
            <SearchTypeSelect
                classes={{ root: classes.typeFieldRoot, list: classes.typeFieldList }}
                value={type}
                onChange={handleTypeChange}
            />
            <Divider orientation={'vertical'} flexItem className={classes.divider} />
            {[SearchType.simple, SearchType.elastic].includes(type) && (
                <AutoCompleteInput
                    id={`__${type}_autocomplete_input__`}
                    ref={autoCompleteInputRef}
                    loading={loading}
                    placeholder="Searching through 3M+ vulnerabilities and exploits"
                    className={inputClassName}
                    classes={{ root: classes.searchFieldRoot, list: cx(classes.searchFieldList, listClassName) }}
                    value={query}
                    onChange={handleQueryChange}
                    onKeyDown={handleAutoCompleteKeyDown}
                    options={suggestions}
                    onOptionClick={onOptionClick}
                    data-testid={`${type}SearchInput`}
                />
            )}
            {type === SearchType.product && (
                <>
                    <AutoCompleteInput
                        id={`__${type}_autocomplete_input__`}
                        ref={autoCompleteInputRef}
                        loading={loading}
                        placeholder="Search by product"
                        className={inputClassName}
                        classes={{ root: classes.searchFieldRoot, list: cx(classes.searchFieldList, listClassName) }}
                        value={software}
                        onChange={handleSoftwareChange}
                        onKeyDown={handleAutoCompleteKeyDown}
                        options={suggestions}
                        onOptionClick={onOptionClick}
                        data-testid={`${type}SearchProductInput`}
                        error={errors.software}
                    />
                    <Divider orientation={'vertical'} flexItem className={classes.divider} />
                    <BaseInput
                        value={version}
                        onChange={handleVersionChange}
                        onKeyDown={handleAutoCompleteKeyDown}
                        placeholder="Version"
                        className={cx(classes.searchFieldRoot, inputClassName)}
                        data-testid={`${type}SearchVersionInput`}
                        error={errors.version}
                    />
                </>
            )}
            <div className={classes.buttonGroup}>
                <SubscriptionButton />
                <ClearButton onClick={handleReset} visible={canClear} />
                <SearchButton
                    onClick={handleSubmit}
                    disabled={typeof onSubmit !== 'function'}
                    data-testid={'search-button'}
                />
                <HotKey />
            </div>
        </div>
    );
};

export default React.memo(DesktopSearchField);
